/**
 * Temas a seguir: chips en cada reunión (cuántas veces salió cada tema; un clic
 * recorre las menciones) y su administración en Ajustes.
 */
import { useMemo, useState } from 'react'
import type { TopicTracker, TranscriptSegment } from '@eco/shared'
import { useAsync } from '../lib/hooks'
import { trackerHits } from '../lib/transcript'
import { errorText, useApp, useToast } from './AppContext'

/** Chips de temas en el detalle; solo aparecen los que salieron en la reunión. */
export function TrackerChips({
  segments,
  onJump
}: {
  segments: TranscriptSegment[]
  onJump: (ms: number) => void
}) {
  const { api } = useApp()
  const trackers = useAsync(() => api.trackers().catch(() => [] as TopicTracker[]), [api])
  const [pos, setPos] = useState<Record<string, number>>({})
  const hits = useMemo(
    () =>
      (trackers.data ?? [])
        .map((t) => ({ t, idx: trackerHits(segments, t.terminos) }))
        .filter((h) => h.idx.length > 0),
    [trackers.data, segments]
  )
  if (!hits.length) return null
  return (
    <div className="row wrap" style={{ gap: 6 }}>
      <span className="faint" style={{ fontSize: 12 }}>
        Temas
      </span>
      {hits.map(({ t, idx }) => (
        <button
          key={t.id}
          className="chip"
          title={`Ir a la siguiente mención de: ${t.terminos.join(', ')}`}
          onClick={() => {
            const i = pos[t.id] ?? 0
            onJump(segments[idx[i % idx.length]!]!.inicioMs)
            setPos((p) => ({ ...p, [t.id]: i + 1 }))
          }}
        >
          {t.nombre} · {idx.length}
        </button>
      ))}
    </div>
  )
}

/** Ajustes → Temas a seguir. */
export function TrackersSection() {
  const { api } = useApp()
  const toast = useToast()
  const list = useAsync(() => api.trackers(), [api])
  const [nombre, setNombre] = useState('')
  const [terminos, setTerminos] = useState('')
  const [adding, setAdding] = useState(false)

  async function add() {
    if (adding) return
    const ts = terminos
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean)
    setAdding(true)
    try {
      await api.createTracker({ nombre: nombre.trim(), terminos: ts })
      setNombre('')
      setTerminos('')
      await list.reload()
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setAdding(false)
    }
  }

  async function remove(t: TopicTracker) {
    try {
      await api.deleteTracker(t.id)
      await list.reload()
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }

  return (
    <section className="card stack">
      <h2>Temas a seguir</h2>
      <p className="faint" style={{ fontSize: 12.5, marginTop: -6 }}>
        Palabras que quieres ver en cada reunión (competidores, precios, un proyecto). Aparecen como
        chips en el detalle y en la biblioteca para buscar en todas.
      </p>
      {list.data?.map((t) => (
        <div key={t.id} className="row wrap">
          <span className="grow">
            <strong>{t.nombre}</strong>{' '}
            <span className="faint" style={{ fontSize: 12.5 }}>
              · {t.terminos.join(', ')}
            </span>
          </span>
          <button className="btn sm ghost danger" onClick={() => void remove(t)}>
            Borrar
          </button>
        </div>
      ))}
      <form
        className="row wrap"
        onSubmit={(e) => {
          e.preventDefault()
          void add()
        }}
      >
        <input
          aria-label="Nombre del tema"
          placeholder="Tema, p. ej. Competencia"
          value={nombre}
          maxLength={40}
          onChange={(e) => setNombre(e.target.value)}
          style={{ flex: '1 1 140px' }}
        />
        <input
          aria-label="Palabras del tema, separadas por coma"
          placeholder="Palabras separadas por coma"
          value={terminos}
          maxLength={300}
          onChange={(e) => setTerminos(e.target.value)}
          style={{ flex: '2 1 220px' }}
        />
        <button className="btn" disabled={adding || !nombre.trim() || !terminos.trim()}>
          Agregar
        </button>
      </form>
    </section>
  )
}
